// views/Dashboard/PainelExecutivo/sections/geradoras/useGeradorasDashboardData.ts
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../../../src/lib/supabase';
import { calcularJanelaPeriodoDashboard } from '../../../../../src/lib/dashboard/periodoDashboard';
import {
  mesesDoIntervalo,
  datasCorteParaRpc,
  calcularGeracaoMensal,
  calcularEsperadoMensal,
} from '../../../../../src/lib/dashboard/geracaoMensal';
import type { LinhaEnergiaAcumulada } from '../../../../../src/lib/dashboard/geracaoMensal';
import { mesRefNaJanela } from '../../../../../src/lib/financeiro/periodo';
import { normalizeMesReferencia } from '../../../../../src/lib/mesReferencia';
import type { PeriodoDashboard } from '../../types';

const FATOR_PADRAO = 4.5;

interface UnidadeGeradoraCapacidade {
  capacidade_instalada: number | null;
}
interface FaturaEnergiaInjetada {
  mes_referencia: string | null;
  energia_injetada_gd1_kwh: number | null;
  energia_injetada_gd2_kwh: number | null;
}
interface RateioUnidadeGeradoraAtiva {
  id: string;
}
interface SaldoAncoraUg {
  rateio_unidade_geradora_id: string;
  saldo_ancora_kwh: number | null;
}

export interface PontoGeradoras {
  mesReferencia: string;
  energiaGeradaKwh: number;
  energiaEsperadaKwh: number;
}

// `periodo` null = hook não busca nada e devolve estado zerado — usado pro
// Período B do comparativo (ver Task 5 do Plano 6). Regra dos Hooks do React:
// chame este hook sempre, nunca dentro de um `if` — passe `null` quando não
// quiser buscar.
export function useGeradorasDashboardData(periodo: PeriodoDashboard | null) {
  const [pontos, setPontos] = useState<PontoGeradoras[]>([]);
  const [energiaInjetadaKwh, setEnergiaInjetadaKwh] = useState(0);
  const [saldoAcumuladoKwh, setSaldoAcumuladoKwh] = useState(0);
  const [loading, setLoading] = useState(!!periodo);
  const [erro, setErro] = useState<string | null>(null);
  const hoje = useMemo(() => new Date(), []);

  const janela = useMemo(() => (periodo ? calcularJanelaPeriodoDashboard(periodo, hoje) : null), [periodo, hoje]);
  const meses = useMemo(() => (periodo ? mesesDoIntervalo(janela, hoje) : []), [periodo, janela, hoje]);

  const carregar = useCallback(async () => {
    if (!periodo) {
      setPontos([]);
      setEnergiaInjetadaKwh(0);
      setSaldoAcumuladoKwh(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErro(null);
    try {
      const datas = datasCorteParaRpc(meses);

      const [
        { data: acumuladoData, error: acumuladoErr },
        { data: unidadesData, error: unidadesErr },
        { data: settingData, error: settingErr },
        { data: faturasData, error: faturasErr },
        { data: ugsAtivasData, error: ugsErr },
        { data: saldosData, error: saldosErr },
      ] = await Promise.all([
        supabase.rpc('get_energia_acumulada_em_datas', { datas }),
        supabase.from('unidades_geradoras').select('capacidade_instalada'),
        supabase.from('system_settings').select('value').eq('key', 'geradoras_fator_geracao_kwh_por_kwp_dia').maybeSingle(),
        supabase.from('faturas').select('mes_referencia, energia_injetada_gd1_kwh, energia_injetada_gd2_kwh').eq('ambiente', 'producao').neq('status_fatura', 'Excluído'),
        supabase.from('rateio_unidades_geradoras').select('id').eq('ativo', true),
        supabase.from('rateio_ancora_saldo_ug').select('rateio_unidade_geradora_id, saldo_ancora_kwh'),
      ]);
      if (acumuladoErr) throw acumuladoErr;
      if (unidadesErr) throw unidadesErr;
      if (settingErr) throw settingErr;
      if (faturasErr) throw faturasErr;
      if (ugsErr) throw ugsErr;
      if (saldosErr) throw saldosErr;

      // Energia Gerada + Real vs. Esperado
      const linhas = (acumuladoData || []) as LinhaEnergiaAcumulada[];
      const geracao = calcularGeracaoMensal(meses, linhas);
      const capacidadeTotalMwp = ((unidadesData || []) as UnidadeGeradoraCapacidade[]).reduce((s, u) => s + (u.capacidade_instalada || 0), 0);
      const fatorSalvo = Number(settingData?.value);
      const fator = Number.isFinite(fatorSalvo) && fatorSalvo > 0 ? fatorSalvo : FATOR_PADRAO;
      const esperado = calcularEsperadoMensal(meses, capacidadeTotalMwp, fator);
      setPontos(geracao.map((g, i) => ({ ...g, energiaEsperadaKwh: esperado[i]?.energiaEsperadaKwh ?? 0 })));

      // Energia Injetada — mesma fonte/regra do Financeiro, somada dentro da janela do período
      const injetadaTotal = ((faturasData || []) as FaturaEnergiaInjetada[])
        .filter((f) => mesRefNaJanela(normalizeMesReferencia(f.mes_referencia), janela))
        .reduce((s, f) => s + (Number(f.energia_injetada_gd1_kwh) || 0) + (Number(f.energia_injetada_gd2_kwh) || 0), 0);
      setEnergiaInjetadaKwh(injetadaTotal);

      // Saldo Acumulado — foto atual, só UGs ativas, não filtrado por período
      const idsAtivos = new Set(((ugsAtivasData || []) as RateioUnidadeGeradoraAtiva[]).map((u) => u.id));
      const saldoTotal = ((saldosData || []) as SaldoAncoraUg[])
        .filter((s) => idsAtivos.has(s.rateio_unidade_geradora_id))
        .reduce((s, r) => s + (Number(r.saldo_ancora_kwh) || 0), 0);
      setSaldoAcumuladoKwh(saldoTotal);
    } catch (e: any) {
      console.error('Erro ao carregar seção Geradoras do Painel Executivo:', e);
      setErro(e?.message || 'Erro ao carregar os dados de geradoras.');
    } finally {
      setLoading(false);
    }
  }, [periodo, meses, janela]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  return { pontos, energiaInjetadaKwh, saldoAcumuladoKwh, loading, erro, refetch: carregar };
}
